import React, { useState, useMemo, useRef, useEffect } from 'react';
import { createPortal, flushSync } from 'react-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import toast from 'react-hot-toast';
import SampleLabelSticker from '@/components/sample/SampleLabelSticker';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import SearchableSelect from '@/components/ui/SearchableSelect';
import {
  Plus, Search, Edit2, Trash2, FlaskConical,
  ChevronRight, ChevronDown, CheckCircle2, RotateCcw, XCircle, Clock, ChevronUp,
  ArrowUpDown, ChevronDown as ChevronDownSort, AlertTriangle, TrendingUp, TrendingDown, BarChart3,
  UserCircle2, MapPin, Truck, Camera, Printer, Copy, FileText, PenTool, Lightbulb,
  Sparkles, Package, Box, AlertCircle, Layers, Check
} from 'lucide-react';
import { needsLiterConversion, formatGramMl } from '@/lib/unitConverter';
import SampleForm from '@/components/sample/SampleForm';
import DataBibitSampleTab from '@/components/sample/DataBibitSampleTab';
import LastUpdated from '@/components/ui/LastUpdated';
import { useSession } from '@/lib/SessionContext';
import { getPageAccess } from '@/lib/AuthSession';
import { useAsaPersonality } from '@/hooks/useAsaPersonality';
import { useBatchSelection } from '@/hooks/useBatchSelection';
import BatchActionBar from '@/components/ui/BatchActionBar';
import { Checkbox } from '@/components/ui/checkbox';

// ── Helpers ────────────────────────────────────────────────────────────────

const STATUS_CONFIG = {
  pending:          { label: 'Pending',             cls: 'bg-amber-100 text-amber-700',     icon: Clock },
  dikerjakan:       { label: 'Sedang Dikerjakan',   cls: 'bg-blue-100 text-blue-700',       icon: CheckCircle2 },
  menunggu_dikirim: { label: 'Menunggu Dikirim',    cls: 'bg-purple-100 text-purple-700',   icon: Clock },
  dikirim:          { label: 'Sudah Dikirim',       cls: 'bg-teal-100 text-teal-700',       icon: CheckCircle2 },
  acc:              { label: 'ACC ✓',               cls: 'bg-green-100 text-green-700',     icon: CheckCircle2 },
  revisi:           { label: 'Revisi',              cls: 'bg-orange-100 text-orange-700',   icon: RotateCcw },
  ditolak:          { label: 'Ditolak',             cls: 'bg-red-100 text-red-700',         icon: XCircle },
};

const KATEGORI_SAMPLE_BADGE = {
  SPL:         'bg-purple-100 text-purple-700',
  'Tes Aroma': 'bg-pink-100 text-pink-700',
  Lainnya:     'bg-slate-100 text-slate-700',
};

const SORT_OPTIONS = [
  { key: 'terbaru', label: 'Terbaru' },
  { key: 'terlama', label: 'Terlama' },
  { key: 'az', label: 'A → Z (Brand)' },
  { key: 'za', label: 'Z → A (Brand)' },
];

function applySort(list, sortKey) {
  const arr = [...list];
  switch (sortKey) {
    case 'terbaru': return arr.sort((a, b) => new Date(b.created_date || 0) - new Date(a.created_date || 0));
    case 'terlama': return arr.sort((a, b) => new Date(a.created_date || 0) - new Date(b.created_date || 0));
    case 'az': return arr.sort((a, b) => (a.nama_brand || '').localeCompare(b.nama_brand || '', 'id'));
    case 'za': return arr.sort((a, b) => (b.nama_brand || '').localeCompare(a.nama_brand || '', 'id'));
    default: return arr;
  }
}

// Normalize record: ensure products[] array always exists
function normalizeRecord(item) {
  if (item.products && Array.isArray(item.products) && item.products.length > 0) {
    return item;
  }
  // Legacy single-product record
  return {
    ...item,
    products: [{
      _id: item.id + '_p0',
      nama_produk: item.nama_produk || '',
      ukuran_ml: item.ukuran_ml || '',
      label: item.label || 'A',
      racikan: Array.isArray(item.racikan) ? item.racikan : [],
      status: item.status || 'pending',
      catatan_review: item.catatan_review || '',
    }]
  };
}

// Calculate overall status from product statuses
function calcOverallStatus(products) {
  if (!products || products.length === 0) return 'pending';
  const statuses = products.map(p => p.status || 'pending');
  if (statuses.every(s => s === 'acc')) return 'acc';
  if (statuses.every(s => s === 'dikerjakan' || s === 'menunggu_dikirim' || s === 'dikirim' || s === 'acc')) {
    if (statuses.some(s => s === 'dikerjakan')) return 'dikerjakan';
    if (statuses.some(s => s === 'menunggu_dikirim')) return 'menunggu_dikirim';
    if (statuses.some(s => s === 'dikirim')) return 'dikirim';
    return 'acc';
  }
  if (statuses.some(s => s === 'ditolak')) return 'ditolak';
  if (statuses.some(s => s === 'revisi')) return 'revisi';
  if (statuses.some(s => s === 'dikerjakan')) return 'dikerjakan';
  if (statuses.some(s => s === 'menunggu_dikirim')) return 'menunggu_dikirim';
  if (statuses.some(s => s === 'dikirim')) return 'dikirim';
  return 'pending';
}

// Build status summary string like "1 ACC, 2 Revisi"
function statusSummary(products) {
  if (!products || products.length <= 1) return null;
  const counts = {};
  products.forEach(p => {
    const s = p.status || 'pending';
    counts[s] = (counts[s] || 0) + 1;
  });
  const parts = [];
  if (counts.acc) parts.push(`${counts.acc} ACC`);
  if (counts.dikirim) parts.push(`${counts.dikirim} Dikirim`);
  if (counts.menunggu_dikirim) parts.push(`${counts.menunggu_dikirim} Menunggu`);
  if (counts.dikerjakan) parts.push(`${counts.dikerjakan} Dikerjakan`);
  if (counts.revisi) parts.push(`${counts.revisi} Revisi`);
  if (counts.ditolak) parts.push(`${counts.ditolak} Ditolak`);
  if (counts.pending) parts.push(`${counts.pending} Pending`);
  return parts.join(', ');
}

function SortDropdown({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const current = SORT_OPTIONS.find(s => s.key === value);
  return (
    <div className="relative">
      <button
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-border bg-background text-xs font-medium text-muted-foreground hover:border-accent/60 transition-all"
      >
        <ArrowUpDown className="w-3.5 h-3.5" />
        {current?.label || 'Urutkan'}
        <ChevronDownSort className="w-3 h-3" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-1 z-20 bg-card border border-border rounded-xl shadow-lg py-1 min-w-[160px]">
            {SORT_OPTIONS.map(opt => (
              <button key={opt.key} onClick={() => { onChange(opt.key); setOpen(false); }}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-muted/60 transition-colors ${value === opt.key ? 'text-accent font-semibold' : 'text-foreground'}`}>
                {opt.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ── Product Sub-Card (inside expanded view) ────────────────────────────

function ProductSubCard({ product, productIndex, sampleId, canEdit, onStatusChange, onAccClick, onDikerjakanClick, onEditClick, onPrintClick, isAccing, currentMemberName }) {
  const [expanded, setExpanded] = useState(false);
  const statusCfg = STATUS_CONFIG[product.status] || STATUS_CONFIG.pending;
  const racikanArray = Array.isArray(product.racikan) ? product.racikan : [];
  const totalPersen = racikanArray.reduce((s, r) => s + (Number(String(r.persentase).replace(',', '.')) || 0), 0);

  return (
    <div className="border border-border rounded-xl overflow-hidden bg-white">
      {/* Sub-card header */}
      <div className="flex items-center gap-2 md:gap-3 px-3 md:px-4 py-2.5 md:py-3">
        <div className="w-7 h-7 rounded-full bg-[#8C6208] text-white flex items-center justify-center text-sm font-bold shrink-0">
          {productIndex + 1}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-sm">{product.nama_produk || 'Produk'}</span>
            <Badge variant="outline" className="text-[9px] font-mono bg-slate-50 text-slate-500 px-1.5 py-0">
              {String(sampleId).slice(-4).toUpperCase()}-{String(product._id).slice(-4).toUpperCase()}
            </Badge>
            {product.ukuran_ml && <Badge variant="outline" className="text-xs">{product.ukuran_ml}ml</Badge>}
            {product.label && (
              <span className="w-6 h-6 rounded-md bg-primary/10 flex items-center justify-center text-primary font-bold text-xs">
                {product.label}
              </span>
            )}
            <Badge className={`text-xs ${statusCfg.cls}`}>{statusCfg.label}</Badge>
          </div>
          <p className="text-xs text-muted-foreground mt-0.5">
            {racikanArray.length} bahan · {totalPersen.toFixed(1)}%
            {product.catatan_review && ` · Review: ${product.catatan_review}`}
            {product.dikerjakan_oleh && (
              <span className="ml-1 text-teal-600 font-medium">· Dikerjakan oleh: {product.dikerjakan_oleh}</span>
            )}
          </p>
        </div>

        <div className="flex gap-1 shrink-0 items-center">
          {canEdit && (
            <Button
              size="icon" variant="ghost" className="w-7 h-7 text-muted-foreground hover:text-amber-600 hover:bg-amber-50"
              onClick={() => onEditClick(productIndex)}
            >
              <Edit2 className="w-3.5 h-3.5" />
            </Button>
          )}
          <Button
            size="icon" variant="ghost" className="w-7 h-7 text-muted-foreground hover:text-slate-700 hover:bg-slate-100"
            onClick={() => onPrintClick(productIndex)}
            title="Cetak Stiker QR"
          >
            <Printer className="w-3.5 h-3.5" />
          </Button>
          {/* Kerjakan Button */}
          {product.status === 'pending' && canEdit && (
            <Button
              size="sm" variant="outline"
              className="h-7 text-xs gap-1 text-blue-700 border-blue-300 hover:bg-blue-50 hover:border-blue-500"
              onClick={() => onDikerjakanClick(productIndex)}
            >
              <FlaskConical className="w-3.5 h-3.5" />
              Kerjakan
            </Button>
          )}
          {/* Selesai Button */}
          {product.status === 'dikerjakan' && canEdit && (
            <Button
              size="sm" variant="outline"
              className="h-7 text-xs gap-1 text-purple-700 border-purple-300 hover:bg-purple-50 hover:border-purple-500"
              onClick={() => onStatusChange(productIndex, 'menunggu_dikirim')}
            >
              <CheckCircle2 className="w-3.5 h-3.5" />
              Selesai
            </Button>
          )}
          {/* Sudah Dikirim Button */}
          {product.status === 'menunggu_dikirim' && canEdit && (
            <Button
              size="sm" variant="outline"
              className="h-7 text-xs gap-1 text-teal-700 border-teal-300 hover:bg-teal-50 hover:border-teal-500"
              onClick={() => onStatusChange(productIndex, 'dikirim')}
            >
              <CheckCircle2 className="w-3.5 h-3.5" />
              Kirim
            </Button>
          )}
          {/* Per-product ACC button (only after it's sent) */}
          {product.status === 'dikirim' && canEdit && (
            <Button
              size="sm" variant="outline"
              className="h-7 text-xs gap-1 text-green-700 border-green-300 hover:bg-green-50 hover:border-green-500"
              disabled={isAccing}
              onClick={() => onAccClick(productIndex)}
            >
              <CheckCircle2 className="w-3.5 h-3.5" />
              {isAccing ? '...' : 'ACC'}
            </Button>
          )}
          <button type="button" onClick={() => setExpanded(e => !e)} className="p-1.5 rounded-md hover:bg-muted/60 transition-colors">
            {expanded ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
          </button>
        </div>
      </div>

      {/* Sub-card expanded body */}
      {expanded && (
        <div className="border-t border-border bg-muted/10 p-4 space-y-4">
          {product.catatan_review && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 text-xs text-blue-700">
              Catatan Review: {product.catatan_review}
            </div>
          )}

          {racikanArray.length > 0 ? (
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Komposisi Racikan</p>
              <div className="rounded-xl border border-border overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="text-left px-3 py-2 text-xs font-semibold text-muted-foreground">Kategori</th>
                      <th className="text-left px-3 py-2 text-xs font-semibold text-muted-foreground">Vendor</th>
                      <th className="text-left px-3 py-2 text-xs font-semibold text-muted-foreground">Bahan</th>
                      <th className="text-right px-3 py-2 text-xs font-semibold text-muted-foreground">%</th>
                      <th className="text-right px-3 py-2 text-xs font-semibold text-muted-foreground">Volume</th>
                      <th className="text-center px-3 py-2 text-xs font-semibold text-muted-foreground">Sumber</th>
                    </tr>
                  </thead>
                  <tbody>
                    {racikanArray.map((r, i) => {
                      const isLiter = needsLiterConversion('', r.kategori_bahan);
                      const gramMl = isLiter && r.volume_ml > 0 ? formatGramMl(r.volume_ml) : null;
                      return (
                        <tr key={i} className="border-t border-border/50">
                          <td className="px-3 py-2 text-xs"><Badge variant="outline" className="text-xs">{r.kategori_bahan}</Badge></td>
                          <td className="px-3 py-2 text-xs text-muted-foreground">
                            {r.vendor || '—'}
                            {r.grade && <Badge className="ml-1 text-[10px] bg-amber-100 text-amber-700">{r.grade}</Badge>}
                          </td>
                          <td className="px-3 py-2 text-xs font-medium">
                            {r.nama_bahan || (r.dari_client ? 'Bibit dari Client' : '')}
                          </td>
                          <td className="px-3 py-2 text-xs text-right font-semibold">{r.persentase}%</td>
                          <td className="px-3 py-2 text-xs text-right">
                            {gramMl ? (
                              <span className="text-orange-600 font-semibold">{gramMl.gramLabel} / {gramMl.mlLabel}</span>
                            ) : (
                              <span className="text-muted-foreground">{r.volume_ml}ml</span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-xs text-center">
                            {r.dari_client ? (
                              <Badge className="text-[10px] bg-amber-100 text-amber-700">Client</Badge>
                            ) : (
                              <span className="text-muted-foreground">Pabrik</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">Belum ada racikan dicatat.</p>
          )}

          {/* Per-product status buttons */}
          {canEdit && (
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Update Status Produk Ini</p>
              {product.dikerjakan_oleh && (
                <div className="mb-2 flex items-center gap-1.5 text-xs text-teal-700 bg-teal-50 border border-teal-200 rounded-lg px-3 py-1.5">
                  <UserCircle2 className="w-3.5 h-3.5" />
                  <span>Dikerjakan oleh <strong>{product.dikerjakan_oleh}</strong></span>
                  {product.dikerjakan_pada && <span className="text-teal-500 ml-1">· {product.dikerjakan_pada}</span>}
                </div>
              )}
              <div className="flex gap-2 flex-wrap">
                {['pending', 'dikerjakan', 'menunggu_dikirim', 'dikirim', 'acc', 'revisi', 'ditolak'].map(s => {
                  const cfg = STATUS_CONFIG[s];
                  return (
                    <button key={s}
                      onClick={() => {
                        if (s === 'acc') onAccClick(productIndex);
                        else if (s === 'dikerjakan') onDikerjakanClick(productIndex);
                        else onStatusChange(productIndex, s);
                      }}
                      className={`px-3 py-1.5 rounded-lg border text-xs font-medium transition-all ${product.status === s ? cfg.cls + ' border-current' : 'border-border text-muted-foreground hover:border-accent/50'}`}>
                      {cfg.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Sub: Sample ────────────────────────────────────────────────────────────

function SampleTab() {
  const qc = useQueryClient();
  const [search, setSearch] = useState(() => {
    if (typeof window !== 'undefined') {
      const p = new URLSearchParams(window.location.search);
      return p.get('search') || '';
    }
    return '';
  });

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const p = new URLSearchParams(window.location.search);
      const q = p.get('search');
      if (q) setSearch(q);
    }
  }, []);
  const [statusFilter, setStatusFilter] = useState('semua');
  const [sort, setSort] = useState('terbaru');
  const [showForm, setShowForm] = useState(false);

  // Pagination & Scroll State
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  const [editItem, setEditItem] = useState(null);
  const [editProductIndex, setEditProductIndex] = useState(undefined);
  const [expandedId, setExpandedId] = useState(null);
  const [accingProductKey, setAccingProductKey] = useState(null); // "sampleId_productIndex"
  const [dupNotif, setDupNotif] = useState(null);
  const [accPrompt, setAccPrompt] = useState(null); // { sampleItem, productIndex, tempUkuran }
  const [invoiceSyncPrompt, setInvoiceSyncPrompt] = useState(null); // { targetPO, sampleItem, productIndex, product, finalSize, existingRacikanId }

  const { member } = useSession();
  const { sapaan } = useAsaPersonality();
  const canEdit = getPageAccess(member, "sample_racikan") === "edit";

  const { data: rawSampleList = [], isLoading } = useQuery({
    queryKey: ['sample-racikan'],
    queryFn: () => base44.entities.SampleRacikan.list('-created_date'),
  });

  // Normalize all records
  const sampleList = useMemo(() => rawSampleList.map(normalizeRecord), [rawSampleList]);

  const [batchPrintItems, setBatchPrintItems] = useState([]);

  const onPrintClick = (sampleItem, productIndex) => {
    toast.success('Menyiapkan stiker...', { duration: 1500 });
    flushSync(() => {
      setBatchPrintItems([{ sample: sampleItem, product: sampleItem.products[productIndex] }]);
    });
    setTimeout(() => { window.print(); }, 100);
  };

  const { data: racikanDbList = [] } = useQuery({
    queryKey: ['racikan-database'],
    queryFn: () => base44.entities.RacikanDatabase.list(),
  });

  const { data: bahanCairList = [] } = useQuery({
    queryKey: ['bahan-cair'],
    queryFn: () => base44.entities.BahanCair.list().catch(() => []),
  });

  const { data: bibitList = [] } = useQuery({
    queryKey: ['bibit-sample'],
    queryFn: () => base44.entities.BibitSample.list().catch(() => []),
  });

  const { data: botolList = [] } = useQuery({
    queryKey: ['botol'],
    queryFn: () => base44.entities.Botol.list().catch(() => []),
  });

  const { data: fullsetList = [] } = useQuery({
    queryKey: ['fullset'],
    queryFn: () => base44.entities.Fullset.list().catch(() => []),
  });

  const { data: tutupList = [] } = useQuery({
    queryKey: ['tutup'],
    queryFn: () => base44.entities.Tutup.list().catch(() => []),
  });

  const { data: sprayList = [] } = useQuery({
    queryKey: ['spray'],
    queryFn: () => base44.entities.Spray.list().catch(() => []),
  });

  const { data: dbBahanList = [] } = useQuery({
    queryKey: ['database-bahan'],
    queryFn: () => base44.entities.DatabaseBahan.list().catch(() => []),
  });

  const delMut = useMutation({
    mutationFn: id => base44.entities.SampleRacikan.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sample-racikan'] }),
  });

  const {
    selectedIds,
    isSelectionMode,
    toggleSelection,
    selectAll,
    clearSelection,
    startSelection,
    selectedCount,
    totalCount
  } = useBatchSelection(sampleList, s => s.id);

  const handleOpenBatchPrintPreview = () => {
    toast.success('Menyiapkan stiker...', { duration: 1500 });
    const items = [];
    sampleList.forEach(s => {
      if (selectedIds.has(s.id)) {
        (s.products || []).forEach(p => {
          items.push({ sample: s, product: p });
        });
      }
    });
    flushSync(() => {
      setBatchPrintItems(items);
    });
    setTimeout(() => { window.print(); }, 100);
  };

  const batchDeleteMutation = useMutation({
    mutationFn: async (ids) => {
      for (const id of ids) {
        await base44.entities.SampleRacikan.delete(id);
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sample-racikan'] });
      clearSelection();
    }
  });

  const batchUpdateStatusMutation = useMutation({
    mutationFn: async ({ ids, newStatus }) => {
      const memberName = member?.full_name || 'System';
      const now = new Date().toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
      
      for (const id of ids) {
        const sample = sampleList.find(s => s.id === id);
        if (!sample) continue;
        
        const updatedProducts = (sample.products || []).map(p => {
          // If the product is already finished (acc/revisi/ditolak), don't overwrite it with a lower status batch action, unless explicitly desired. But for simplicity, we just override.
          const updates = { ...p, status: newStatus };
          if (newStatus === 'dikerjakan') {
            updates.dikerjakan_oleh = memberName;
            updates.dikerjakan_pada = now;
          }
          return updates;
        });
        
        const overallStatus = calcOverallStatus(updatedProducts);
        await base44.entities.SampleRacikan.update(id, { products: updatedProducts, status: overallStatus });
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sample-racikan'] });
      clearSelection();
    }
  });

  const updateMut = useMutation({
    mutationFn: ({ id, data }) => base44.entities.SampleRacikan.update(id, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sample-racikan'] }),
  });

  const createRacikanMut = useMutation({
    mutationFn: (data) => base44.entities.RacikanDatabase.create(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['racikan-database'] });
      setAccingProductKey(null);
    },
  });

  const updateRacikanMut = useMutation({
    mutationFn: ({ id, data }) => base44.entities.RacikanDatabase.update(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['racikan-database'] });
      setAccingProductKey(null);
      setDupNotif(null);
    },
  });

  const createBahanMut = useMutation({
    mutationFn: (data) => base44.entities.DatabaseBahan.create(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['database-bahan'] }),
  });

  const updateBahanMut = useMutation({
    mutationFn: ({ id, data }) => base44.entities.DatabaseBahan.update(id, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['database-bahan'] }),
  });

  // Update a single product's status within a sample
  const handleProductStatusChange = (sampleItem, productIndex, newStatus) => {
    const updatedProducts = sampleItem.products.map((p, i) => 
      i === productIndex ? { ...p, status: newStatus } : p
    );
    const overallStatus = calcOverallStatus(updatedProducts);
    updateMut.mutate({
      id: sampleItem.id,
      data: { products: updatedProducts, status: overallStatus }
    });
  };

  // Mark a product as "Sudah Dikerjakan" and record who did it
  const handleDikerjakanClick = (sampleItem, productIndex) => {
    const memberName = member?.full_name || 'System';
    const now = new Date().toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    const updatedProducts = sampleItem.products.map((p, i) => 
      i === productIndex ? { ...p, status: 'dikerjakan', dikerjakan_oleh: memberName, dikerjakan_pada: now } : p
    );
    const overallStatus = calcOverallStatus(updatedProducts);
    updateMut.mutate({
      id: sampleItem.id,
      data: { products: updatedProducts, status: overallStatus }
    });
  };

  const calculatePoModal = (po, allBahan = []) => {
    if (!po) return 0;
    const prodHpp = (po.products || []).reduce((s, p) => {
      const qty = Number(p.qty || p.pcs || p.jumlah_order || 1);
      
      // 1. Modal Packaging (Kemasan: Fullset, Botol, Tutup, Spray, Box, dll)
      const modalPackaging = Number(p.modal_kemasan || p.kemasan_harga_beli || p.fullset_harga_beli || p.botol_harga_beli || 0) 
        + Number(p.tutup_harga_beli || 0) 
        + Number(p.spray_harga_beli || 0);

      // 2. Volume Botol Produk di Invoice (misal: 33 ml)
      const volume = Number(p.botol_ukuran_aktual || p.botol_ukuran_label || p.botol_ukuran_client || p.ukuran_botol_ml || p.volume_ml || p.ukuran_ml || 30);

      // 3. Formula Racikan dihitung per botol berdasarkan % terhadap volume botol di invoice
      const modalRacikan = (p.racikan || []).reduce((rs, r) => {
        if (r.dari_client === true || r.vendor === 'Client') return rs;
        const bMatch = allBahan.find(b => (b.nama || b.nama_bahan || '').trim().toLowerCase() === (r.nama_bahan || r.bahan_nama || '').trim().toLowerCase());
        const costPerKg = Number(r.hpp_bahan || r.harga_per_satuan || bMatch?.harga_beli || bMatch?.harga_satuan || bMatch?.harga_rupiah || bMatch?.harga || 0);
        const persen = parseFloat(String(r.persentase).replace(',', '.')) || 0;
        const volMlPerPcs = (volume * (persen / 100));
        return rs + (volMlPerPcs * (costPerKg / 1000));
      }, 0);

      // 4. Komponen Tambahan & Biaya Custom
      const modalKomponen = (p.komponen_tambahan || []).reduce((ks, k) => ks + (Number(k.harga) || 0), 0);
      const customHargaBeli = Number(p.hargaBeli || p.harga_beli || 0);
      const hppPerPcs = modalPackaging + modalRacikan + modalKomponen + customHargaBeli;
      return s + (hppPerPcs * qty);
    }, 0);

    const legHpp = (po.legalitas || []).reduce((s, l) => s + ((Number(l.harga_beli) || 0) * (Number(l.qty_variant) || 1)), 0);
    const komisiTotal = (po.komisiList || []).reduce((s, k) => s + (Number(k.nominal) || 0), 0);
    return prodHpp + legHpp + komisiTotal;
  };

  // ACC logic:
  // - Jika sample dari invoice: langsung buka konfirmasi update Invoice & hitung perbandingan Before vs After + integrasi inventori
  // - Jika sample manual biasa: prompt ukuran final untuk disimpan ke Database Racikan
  const handleAccClick = async (sampleItem, productIndex) => {
    const product = sampleItem.products[productIndex];
    const isFromInvoice = !!(sampleItem.source_invoice_id || sampleItem.source_invoice_number);

    if (isFromInvoice) {
      try {
        let allBahan = bahanCairList || [];
        if (!allBahan || allBahan.length === 0) {
          try {
            allBahan = await base44.entities.BahanCair.list();
          } catch (e) {
            console.warn('Could not load BahanCair:', e);
          }
        }

        const allPOs = await base44.entities.ClientPO.list();
        const targetPO = allPOs.find(po => 
          po.id === sampleItem.source_invoice_id || 
          po.no_po === sampleItem.source_invoice_number || 
          po.invoice_number === sampleItem.source_invoice_number
        );
        if (targetPO) {
          const oldModal = calculatePoModal(targetPO, allBahan);

          const targetInvoiceProduct = (targetPO.products || []).find(p => 
            (p.nama_product || '').toLowerCase().trim() === (product.nama_produk || '').toLowerCase().trim()
          ) || targetPO.products?.[0] || null;

          const oldRacikan = targetInvoiceProduct?.racikan || [];
          const newRacikan = product.racikan || [];

          // Map integration status for each new ingredient (DPG, PEG, Aqua Des, Alkohol, Bibit, dll)
          const mappedNewRacikan = newRacikan.map(r => {
            const rName = (r.nama_bahan || '').toLowerCase().trim();
            const rKat = (r.kategori_bahan || '').toLowerCase().trim();

            const bMatch = allBahan.find(b => {
              const bName = (b.nama || b.nama_bahan || '').toLowerCase().trim();
              const bKat = (b.kategori || '').toLowerCase().trim();
              if (bName === rName) return true;
              if (rName && (bName.includes(rName) || rName.includes(bName))) return true;
              if (rKat === 'dpg' && (bName.includes('dpg') || bName.includes('dipropylene') || bKat.includes('dpg'))) return true;
              if (rKat === 'peg' && (bName.includes('peg') || bKat.includes('peg'))) return true;
              if (rKat === 'aqua des' && (bName.includes('aqua') || bName.includes('air') || bKat.includes('aqua'))) return true;
              if (rKat === 'sustain' && (bName.includes('sustain') || bKat.includes('sustain'))) return true;
              if (rKat === 'quidex' && (bName.includes('quidex') || bName.includes('guidex') || bKat.includes('quidex'))) return true;
              if (rKat === 'castor oil' && (bName.includes('castor') || bKat.includes('castor'))) return true;
              if (rKat === 'alkohol' && (bName.includes('alkohol') || bName.includes('superfine') || bKat.includes('alkohol'))) return true;
              return false;
            });

            const bibitMatch = (bibitList || []).find(b => {
              const bName = (b.nama_bibit || b.nama_bahan || b.nama || '').toLowerCase().trim();
              if (bName === rName) return true;
              if (rName && (bName.includes(rName) || rName.includes(bName))) return true;
              return false;
            });
            
            let isIntegrated = false;
            let integratedType = '';
            let vendor = r.vendor || '';
            let stok = null;
            let satuan = 'ml';

            if (r.dari_client) {
              isIntegrated = true;
              integratedType = 'Bahan dari Klien';
            } else if (bMatch) {
              isIntegrated = true;
              integratedType = 'Item Inventori Gudang';
              vendor = bMatch.vendor || bMatch.supplier || vendor;
              stok = bMatch.stok ?? bMatch.stock ?? null;
              satuan = bMatch.satuan || 'liter';
            } else if (bibitMatch) {
              isIntegrated = true;
              integratedType = 'Database Bibit Resmi';
              vendor = bibitMatch.vendor || vendor;
              stok = bibitMatch.stok ?? bibitMatch.stock ?? null;
              satuan = 'ml';
            }

            return {
              ...r,
              isIntegrated,
              integratedType,
              vendor,
              stok,
              satuan,
              bMatch,
              bibitMatch
            };
          });

          const simulatedPoProducts = (targetPO.products || []).map(p => {
            if ((p.nama_product || '').toLowerCase().trim() === (product.nama_produk || '').toLowerCase().trim()) {
              const volume = Number(p.botol_ukuran_aktual || p.botol_ukuran_label || p.botol_ukuran_client || p.ukuran_botol_ml || p.volume_ml || p.ukuran_ml || 30);
              const updatedRacikan = (product.racikan || []).map(r => {
                const bMatch = allBahan.find(b => (b.nama || b.nama_bahan || '').trim().toLowerCase() === (r.nama_bahan || '').trim().toLowerCase());
                const bibitMatch = (bibitList || []).find(b => (b.nama_bibit || b.nama_bahan || b.nama || '').trim().toLowerCase() === (r.nama_bahan || '').trim().toLowerCase());
                const hppSat = Number(bMatch?.harga_beli || bMatch?.harga_satuan || bMatch?.harga_rupiah || bMatch?.harga || bibitMatch?.harga_beli || bibitMatch?.harga_satuan || r.hpp_bahan || r.harga_per_satuan || 0);
                const persen = parseFloat(String(r.persentase).replace(',', '.')) || 0;
                const volMlPerPcs = Number(parseFloat((volume * (persen / 100)).toFixed(2)));

                return {
                  kategori: r.kategori_bahan || 'Bibit',
                  kategori_bahan: r.kategori_bahan || 'Bibit',
                  vendor: r.vendor || bMatch?.vendor || bibitMatch?.vendor || '',
                  nama_bahan: r.nama_bahan || '',
                  bahan_nama: r.nama_bahan || '',
                  bahan_id: bMatch?.id || bibitMatch?.id || r.bahan_id || `bahan-${Date.now()}`,
                  persentase: persen,
                  volume_ml: volMlPerPcs,
                  dari_client: !!r.dari_client,
                  is_sample: true,
                  harga_per_satuan: hppSat,
                  hpp_bahan: hppSat
                };
              });
              return { ...p, racikan: updatedRacikan };
            }
            return p;
          });

          const simulatedTargetPo = { ...targetPO, products: simulatedPoProducts };
          const newModal = calculatePoModal(simulatedTargetPo, allBahan);

          const poQty = Number(targetInvoiceProduct?.qty || targetInvoiceProduct?.pcs || 1000);
          const oldHppPerPcs = poQty > 0 ? (oldModal / poQty) : 0;
          const newHppPerPcs = poQty > 0 ? (newModal / poQty) : 0;

          setInvoiceSyncPrompt({
            targetPO,
            sampleItem,
            productIndex,
            product,
            finalSize: product.ukuran_ml || 0,
            existingRacikanId: null,
            oldModal,
            newModal,
            deltaModal: newModal - oldModal,
            oldHppPerPcs,
            newHppPerPcs,
            deltaHppPerPcs: newHppPerPcs - oldHppPerPcs,
            targetInvoiceProduct,
            oldRacikan,
            newRacikan: mappedNewRacikan,
            allBahan
          });
          return;
        }
      } catch (err) {
        console.error('Error finding source ClientPO:', err);
      }
    }

    // Lookup existing brand varian in database
    const existingRacikan = racikanDbList.find(
      r => (r.nama_brand || '').toLowerCase().trim() === (sampleItem.nama_brand || '').toLowerCase().trim()
        && (r.nama_varian || '').toLowerCase().trim() === (product.nama_produk || '').toLowerCase().trim()
    );
    const existingBahan = dbBahanList.find(
      b => (b.nama_brand || '').toLowerCase().trim() === (sampleItem.nama_brand || '').toLowerCase().trim()
        && (b.nama_varian || '').toLowerCase().trim() === (product.nama_produk || '').toLowerCase().trim()
    );

    setAccPrompt({
      sampleItem,
      productIndex,
      product,
      isExisting: Boolean(existingRacikan || existingBahan),
      existingRacikanId: existingRacikan?.id || null,
      existingBahanId: existingBahan?.id || null,
      saveMode: (existingRacikan || existingBahan) ? 'overwrite' : 'new', // 'overwrite' | 'new'
      isFullset: existingBahan?.is_fullset || false,
      fullsetNama: existingBahan?.fullset_nama || '',
      botolNama: existingBahan?.botol_nama || '',
      tutupNama: existingBahan?.tutup_nama || '',
      sprayNama: existingBahan?.spray_nama || '',
      ukuranLabelMl: product.ukuran_ml || existingRacikan?.ukuran_ml || existingBahan?.botol_ukuran_label_ml || 50,
      ukuranAktualMl: existingBahan?.botol_ukuran_aktual_ml || product.ukuran_ml || 50,
      catatan: existingBahan?.catatan || sampleItem.catatan || '',
    });
  };

  const handleManualAccSubmit = async () => {
    if (!accPrompt) return;
    const {
      sampleItem,
      productIndex,
      product,
      saveMode,
      existingRacikanId,
      existingBahanId,
      isFullset,
      fullsetNama,
      botolNama,
      tutupNama,
      sprayNama,
      ukuranLabelMl,
      ukuranAktualMl,
      catatan
    } = accPrompt;

    const key = `${sampleItem.id}_${productIndex}`;
    setAccingProductKey(key);

    try {
      // 1. Update product status in sample
      const updatedProducts = sampleItem.products.map((p, i) =>
        i === productIndex ? { ...product, status: 'acc' } : p
      );
      const overallStatus = calcOverallStatus(updatedProducts);
      await updateMut.mutateAsync({
        id: sampleItem.id,
        data: { products: updatedProducts, status: overallStatus }
      });

      // 2. Prepare Racikan payload
      const racikanPayload = {
        nama_brand: sampleItem.nama_brand,
        nama_varian: product.nama_produk,
        ukuran_ml: Number(ukuranLabelMl) || 0,
        racikan: product.racikan || [],
        catatan: catatan || sampleItem.catatan || '',
        pic_brand: sampleItem.pic_brand || '',
        pic_pembuat: sampleItem.pic_pembuat || '',
        status: 'aktif',
      };

      if (saveMode === 'overwrite' && existingRacikanId) {
        await updateRacikanMut.mutateAsync({ id: existingRacikanId, data: racikanPayload });
      } else {
        await createRacikanMut.mutateAsync(racikanPayload);
      }

      // 3. Prepare DatabaseBahan payload
      const bahanPayload = {
        nama_brand: sampleItem.nama_brand,
        nama_varian: product.nama_produk,
        is_fullset: isFullset,
        fullset_nama: isFullset ? fullsetNama : null,
        botol_nama: isFullset ? 'Satu set dengan botol (Fullset)' : (botolNama || 'Dari Client'),
        botol_ukuran_label_ml: Number(ukuranLabelMl) || 0,
        botol_ukuran_aktual_ml: Number(ukuranAktualMl) || Number(ukuranLabelMl) || 0,
        tutup_nama: isFullset ? 'Satu set dengan botol (Fullset)' : (tutupNama || 'Dari Client'),
        spray_nama: isFullset ? 'Satu set dengan botol (Fullset)' : (sprayNama || 'Dari Client'),
        catatan: catatan || '',
        status: 'aktif',
      };

      if (saveMode === 'overwrite' && existingBahanId) {
        await updateBahanMut.mutateAsync({ id: existingBahanId, data: bahanPayload });
      } else {
        await createBahanMut.mutateAsync(bahanPayload);
      }

      qc.invalidateQueries({ queryKey: ['racikan-database'] });
      qc.invalidateQueries({ queryKey: ['database-bahan'] });
      setAccingProductKey(null);
      setAccPrompt(null);
      toast.success(`Formulasi "${product.nama_produk}" (${sampleItem.nama_brand}) berhasil di-ACC & disimpan ke Database Brand!`);
    } catch (e) {
      console.error('Error submitting manual ACC:', e);
      toast.error('Gagal memproses ACC: ' + (e.message || 'Terjadi kesalahan'));
      setAccingProductKey(null);
    }
  };

  const formatRupiah = (val) => {
    return new Intl.NumberFormat('id-ID', { 
      style: 'currency', 
      currency: 'IDR', 
      minimumFractionDigits: 2, 
      maximumFractionDigits: 2 
    }).format(Number(val) || 0);
  };

  const formatDecimalNumber = (val) => {
    if (val === null || val === undefined || isNaN(val)) return '0';
    const num = Number(val);
    return num.toLocaleString('id-ID', { maximumFractionDigits: 2, minimumFractionDigits: 0 });
  };
  const formatTanggalIndo = (d) => {
    if (!d) return '-';
    try {
      return new Date(d).toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' });
    } catch {
      return d;
    }
  };

  const doAccProduct = async (sampleItem, productIndex, product, finalSize, existingRacikanId) => {
    commitAcc({ sampleItem, productIndex, product, finalSize, existingRacikanId, updateInvoice: false });
  };

  const commitAcc = async ({ sampleItem, productIndex, product, finalSize, existingRacikanId, updateInvoice, targetPO }) => {
    const key = `${sampleItem.id}_${productIndex}`;
    setAccingProductKey(key);

    // Update product status in sample
    const updatedProducts = sampleItem.products.map((p, i) =>
      i === productIndex ? { ...product, status: 'acc' } : p
    );
    const overallStatus = calcOverallStatus(updatedProducts);
    updateMut.mutate({
      id: sampleItem.id,
      data: { products: updatedProducts, status: overallStatus }
    });

    const isFromInvoice = !!(sampleItem.source_invoice_id || sampleItem.source_invoice_number);

    // Hanya simpan ke Database Racikan jika sample BUKAN berasal dari Invoice
    if (!isFromInvoice) {
      const racikanPayload = {
        nama_brand: sampleItem.nama_brand,
        nama_varian: product.nama_produk,
        ukuran_ml: finalSize,
        racikan: product.racikan || [],
        catatan: sampleItem.catatan || '',
        pic_brand: sampleItem.pic_brand || '',
        pic_pembuat: sampleItem.pic_pembuat || '',
        status: 'aktif',
      };
      if (existingRacikanId) {
        updateRacikanMut.mutate({ id: existingRacikanId, data: racikanPayload });
      } else {
        createRacikanMut.mutate(racikanPayload);
      }
    }

    // Jika user konfirmasi update invoice
    if (updateInvoice && targetPO) {
      try {
        let allBahan = [];
        try {
          allBahan = await base44.entities.BahanCair.list();
        } catch (e) {
          console.warn('Could not list BahanCair:', e);
        }

        const updatedPoProducts = (targetPO.products || []).map(p => {
          if ((p.nama_product || '').toLowerCase().trim() === (product.nama_produk || '').toLowerCase().trim()) {
            const volume = Number(p.botol_ukuran_aktual || p.botol_ukuran_label || p.botol_ukuran_client || p.ukuran_botol_ml || p.volume_ml || p.ukuran_ml || 30);
            return {
              ...p,
              racikan: (product.racikan || []).map(r => {
                const bMatch = allBahan.find(b => (b.nama || b.nama_bahan || '').trim().toLowerCase() === (r.nama_bahan || '').trim().toLowerCase());
                const bibitMatch = (bibitList || []).find(b => (b.nama_bibit || b.nama_bahan || b.nama || '').trim().toLowerCase() === (r.nama_bahan || '').trim().toLowerCase());
                const hppSat = Number(bMatch?.harga_beli || bMatch?.harga_satuan || bMatch?.harga_rupiah || bMatch?.harga || bibitMatch?.harga_beli || bibitMatch?.harga_satuan || r.hpp_bahan || r.harga_per_satuan || 0);
                const persen = parseFloat(String(r.persentase).replace(',', '.')) || 0;
                const volMlPerPcs = Number(parseFloat((volume * (persen / 100)).toFixed(2)));

                return {
                  kategori: r.kategori_bahan || 'Bibit',
                  kategori_bahan: r.kategori_bahan || 'Bibit',
                  vendor: r.vendor || bMatch?.vendor || bibitMatch?.vendor || '',
                  nama_bahan: r.nama_bahan || '',
                  bahan_nama: r.nama_bahan || '',
                  bahan_id: bMatch?.id || bibitMatch?.id || r.bahan_id || `bahan-${Date.now()}`,
                  persentase: persen,
                  volume_ml: volMlPerPcs,
                  dari_client: !!r.dari_client,
                  is_sample: true,
                  harga_per_satuan: hppSat,
                  hpp_bahan: hppSat
                };
              })
            };
          }
          return p;
        });
        await base44.entities.ClientPO.update(targetPO.id, { products: updatedPoProducts });
        toast.success(`Formulasi "${product.nama_produk}" berhasil di-ACC & di-update langsung ke Invoice #${targetPO.no_po || targetPO.invoice_number || targetPO.id}!`);
      } catch (err) {
        console.error('Error updating ClientPO formula on ACC:', err);
        toast.error('Gagal memperbarui formulasi ke Invoice.');
      }
    } else if (!isFromInvoice) {
      toast.success(`Formulasi "${product.nama_produk}" berhasil di-ACC & disimpan ke Database Racikan!`);
    } else {
      toast.success(`Sample produk "${product.nama_produk}" berhasil di-ACC!`);
    }

    setInvoiceSyncPrompt(null);
  };

  const q = search.toLowerCase().replace(/\s+/g, '');
  const filtered = useMemo(() => {
    const base = sampleList
      .filter(s => {
        if (statusFilter === 'semua') return true;
        // Filter by overall status OR any product with that status
        const overall = calcOverallStatus(s.products);
        return overall === statusFilter || s.products?.some(p => p.status === statusFilter);
      })
      .filter(s => {
        const brandMatch = (s.nama_brand || '').toLowerCase().includes(q);
        const productMatch = s.products?.some(p => {
          const nameMatch = (p.nama_produk || '').toLowerCase().includes(q);
          // Tambahkan pencarian berdasar ID unik stiker (e.g., A1B2-C3D4)
          const genId = `${String(s.id).slice(-4)}-${String(p._id).slice(-4)}`.toLowerCase();
          const idMatch = genId.includes(q) || genId.replace('-', '').includes(q);
          return nameMatch || idMatch;
        });
        return brandMatch || productMatch;
      });
    return applySort(base, sort);
  }, [sampleList, statusFilter, q, sort]);

  // Count statuses across all products for filter badges
  const statusCounts = useMemo(() => {
    const counts = { semua: sampleList.length, pending: 0, acc: 0, dikerjakan: 0, revisi: 0, ditolak: 0 };
    sampleList.forEach(s => {
      const overall = calcOverallStatus(s.products);
      const matched = new Set();
      matched.add(overall);
      s.products?.forEach(p => matched.add(p.status || 'pending'));
      
      matched.forEach(status => {
        if (counts[status] !== undefined) counts[status]++;
      });
    });
    return counts;
  }, [sampleList]);

  const handleDelete = (item) => {
    if (!confirm(`Hapus sample ${item.nama_brand}?`)) return;
    delMut.mutate(item.id);
  };

  return (
    <div className="space-y-4">
      <BatchActionBar
        isSelectionMode={isSelectionMode}
        selectedCount={selectedCount}
        totalCount={filtered.length}
        onSelectAll={selectAll}
        onClearSelection={clearSelection}
        onBatchDelete={() => {
          if (confirm(`Yakin ingin menghapus ${selectedCount} sample terpilih?`)) {
            batchDeleteMutation.mutate(Array.from(selectedIds));
          }
        }}
        isDeleting={batchDeleteMutation.isPending}
      >
        <div className="mr-2 flex gap-2">
          <Button onClick={handleOpenBatchPrintPreview} variant="outline" className="h-9 gap-2 text-xs text-amber-700 border-amber-300 hover:bg-amber-50" disabled={selectedCount === 0}>
            <Printer className="w-4 h-4" /> Cetak Stiker
          </Button>
          <Select 
            value="" 
            onValueChange={(val) => {
              if (!val) return;
              let statusLabel = '';
              if (val === 'dikerjakan') statusLabel = 'Sedang Dikerjakan';
              if (val === 'menunggu_dikirim') statusLabel = 'Selesai';
              if (val === 'dikirim') statusLabel = 'Sudah Dikirim';
              if (confirm(`Tandai ${selectedCount} sample sebagai '${statusLabel}'?`)) {
                batchUpdateStatusMutation.mutate({ ids: Array.from(selectedIds), newStatus: val });
              }
            }}
            disabled={batchUpdateStatusMutation.isPending || selectedCount === 0}
          >
            <SelectTrigger className="h-9 text-xs font-bold text-slate-700 border-slate-200/80 rounded-xl w-[140px]">
              <SelectValue placeholder="Ubah Status..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="dikerjakan" className="text-xs font-bold text-blue-700">Kerjakan</SelectItem>
              <SelectItem value="menunggu_dikirim" className="text-xs font-bold text-purple-700">Selesai</SelectItem>
              <SelectItem value="dikirim" className="text-xs font-bold text-teal-700">Kirim</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </BatchActionBar>
      
      {/* Dup confirmation banner */}
      {dupNotif && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-amber-800">Data duplikat ditemukan!</p>
              <p className="text-xs text-amber-700 mt-0.5">
                Database Racikan sudah memiliki <strong>{dupNotif.brand} — {dupNotif.varian}</strong>.
                Apakah ingin menimpa data lama dengan racikan sample ini?
              </p>
              <div className="flex gap-2 mt-3">
                <Button size="sm" className="bg-amber-600 hover:bg-amber-700 text-white text-xs gap-1"
                  onClick={() => doAccProduct(dupNotif.sampleItem, dupNotif.productIndex, dupNotif.product, dupNotif.finalSize, dupNotif.existingId)}>
                  Ya, timpa data lama
                </Button>
                <Button size="sm" variant="outline" className="text-xs"
                  onClick={() => {
                    doAccProduct(dupNotif.sampleItem, dupNotif.productIndex, dupNotif.product, dupNotif.finalSize, null);
                    setDupNotif(null);
                  }}>
                  ACC saja, buat baru
                </Button>
                <Button size="sm" variant="ghost" className="text-xs text-muted-foreground" onClick={() => setDupNotif(null)}>
                  Batal
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Toolbar */}
      <div className="flex gap-2 flex-wrap items-center">
        <div className="relative flex-1 min-w-[180px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input placeholder="Cari brand atau produk..." className="pl-9" value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <SortDropdown value={sort} onChange={setSort} />
        {canEdit && (
          <Button onClick={() => { setEditItem(null); setShowForm(true); }} className="gap-2 shrink-0">
            <Plus className="w-4 h-4" /> Tambah Sample
          </Button>
        )}
      </div>

      {/* Status filter */}
      <div className="flex gap-2 flex-wrap">
        {['semua', 'pending', 'dikerjakan', 'menunggu_dikirim', 'dikirim', 'acc', 'revisi', 'ditolak'].map(s => (
          <button key={s} onClick={() => setStatusFilter(s)}
            className={`px-3 py-1.5 md:px-5 md:py-2.5 rounded-full border text-[9px] md:text-xs font-black uppercase tracking-wider transition-all ${statusFilter === s ? 'bg-[#F9F5EC] text-[#8C6208] border-[#E6D5B8] shadow-sm' : 'bg-white text-slate-500 border-slate-200 hover:border-[#E6D5B8]'}`}>
            {s === 'semua' ? `Semua (${statusCounts.semua})` : `${STATUS_CONFIG[s]?.label} (${statusCounts[s] || 0})`}
          </button>
        ))}
      </div>

      {/* List */}
      {isLoading ? (
        <div className="text-center py-10 text-muted-foreground text-sm">Memuat...</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground">
          <FlaskConical className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <p>Belum ada sample. Klik Tambah Sample untuk memulai.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage).map(item => {
            const overallStatus = calcOverallStatus(item.products);
            const statusCfg = STATUS_CONFIG[overallStatus] || STATUS_CONFIG.pending;
            const isExpanded = expandedId === item.id;
            const summary = statusSummary(item.products);
            const productCount = item.products?.length || 0;

            return (
              <Card 
                key={item.id} 
                className={`overflow-hidden transition-all ${
                  isSelectionMode && selectedIds.has(item.id) ? 'ring-2 ring-rose-500 bg-rose-50/20' : ''
                }`}
              >
                <CardContent className="p-0">
                  {/* Brand Header */}
                  <div 
                    className={`flex items-center gap-3 md:gap-4 p-4 md:p-5 lg:p-6 transition-colors ${
                      isSelectionMode ? 'cursor-pointer hover:bg-slate-50' : ''
                    }`}
                    onClick={() => isSelectionMode && toggleSelection(item.id)}
                  >
                    {canEdit && (
                      <div onClick={(e) => e.stopPropagation()}>
                        <Checkbox 
                          checked={selectedIds.has(item.id)} 
                          onCheckedChange={() => toggleSelection(item.id)} 
                          className="mr-2 border-slate-300"
                        />
                      </div>
                    )}
                    {/* Brand icon */}
                    <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center text-primary font-bold text-lg shrink-0">
                      <FlaskConical className="w-5 h-5" />
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-bold text-base">{item.nama_brand}</span>
                        <Badge variant="outline" className="text-xs px-2 py-0.5">{productCount} produk</Badge>
                        <Badge className={`text-xs px-2 py-0.5 ${KATEGORI_SAMPLE_BADGE[item.kategori] || ''}`}>{item.kategori}</Badge>
                        <Badge className={`text-xs px-2 py-0.5 ${statusCfg.cls}`}>{statusCfg.label}</Badge>
                        {item.source_invoice_number || item.source_invoice_id ? (
                          <Badge className="text-[10px] px-2 py-0.5 bg-amber-100 text-amber-900 border-amber-300 font-extrabold shadow-2xs gap-1 inline-flex items-center">
                            <FileText className="w-3 h-3 text-amber-700" />
                            <span>Permintaan Invoice #{item.source_invoice_number || item.source_invoice_id}</span>
                          </Badge>
                        ) : (
                          <Badge className="text-[10px] px-2 py-0.5 bg-slate-100 text-slate-700 border-slate-200 font-bold gap-1 inline-flex items-center">
                            <PenTool className="w-3 h-3 text-slate-500" />
                            <span>Input Manual</span>
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                        {/* Product names preview */}
                        <p className="text-xs text-muted-foreground">
                          {item.products?.map(p => p.nama_produk).filter(Boolean).join(', ')}
                        </p>
                        {/* Status detail summary */}
                        {summary && (
                          <span className="text-[10px] font-medium text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">
                            {summary}
                          </span>
                        )}
                        {item.pic_brand && (
                          <span className="inline-flex items-center gap-1 text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full font-medium" title="PIC Brand">
                            <UserCircle2 className="w-3 h-3" />{item.pic_brand}
                          </span>
                        )}
                        {item.pic_pembuat && (
                          <span className="inline-flex items-center gap-1 text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full font-medium" title="PIC Pembuat (Formulator)">
                            <UserCircle2 className="w-3 h-3" />{item.pic_pembuat}
                          </span>
                        )}
                        <LastUpdated date={item.updated_date || item.created_date} />
                      </div>
                    </div>

                    <div className="flex gap-1.5 shrink-0 items-center">
                      {canEdit && !isSelectionMode && (item.source_invoice_id || item.source_invoice_number || item.created_by_role === 'marketing') && (item.status === 'pending' || !item.status) && (
                        <Button 
                          size="sm" 
                          className="h-8 text-xs font-black gap-1.5 bg-[#8C6208] text-white hover:bg-amber-900 shadow-xs border border-amber-700/40"
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditProductIndex(undefined);
                            setEditItem(item);
                            setShowForm(true);
                          }}
                        >
                          <FlaskConical className="w-3.5 h-3.5" />
                          <span>Isi Racikan / Konfirmasi</span>
                        </Button>
                      )}
                      <Button variant="ghost" size="icon" className="w-8 h-8"
                        onClick={(e) => { e.stopPropagation(); setExpandedId(isExpanded ? null : item.id); }}>
                        {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                      </Button>
                      {canEdit && !isSelectionMode && (
                        <Button variant="ghost" size="icon" className="w-8 h-8 text-blue-600/60 hover:text-blue-600"
                          title="Duplikat ke Brand Baru"
                          onClick={(e) => { 
                            e.stopPropagation(); 
                            const duplicated = { ...item };
                            delete duplicated.id;
                            delete duplicated._id;
                            duplicated.nama_brand = '';
                            duplicated.alamat_pengiriman = null;
                            duplicated.catatan = '';
                            setEditProductIndex(undefined); 
                            setEditItem(duplicated); 
                            setShowForm(true); 
                          }}>
                          <Copy className="w-3.5 h-3.5" />
                        </Button>
                      )}
                      {canEdit && !isSelectionMode && (
                        <Button variant="ghost" size="icon" className="w-8 h-8 text-amber-600/60 hover:text-amber-600"
                          onClick={(e) => { 
                            e.stopPropagation(); 
                            setEditProductIndex(undefined); 
                            setEditItem(item); 
                            setShowForm(true); 
                          }}>
                          <Edit2 className="w-3.5 h-3.5" />
                        </Button>
                      )}
                      {canEdit && !isSelectionMode && (
                        <Button variant="ghost" size="icon" className="w-8 h-8 text-destructive/60 hover:text-destructive"
                          onClick={(e) => { e.stopPropagation(); startSelection(item.id); }}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Expanded: Product sub-cards */}
                  {isExpanded && (
                    <div className="border-t border-border bg-muted/20 p-4 space-y-3">
                      {item.catatan && (
                        <div className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-xs text-slate-600">
                          📝 Catatan: {item.catatan}
                        </div>
                      )}

                      {item.alamat_pengiriman && (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 text-xs text-blue-800 flex items-start gap-2">
                          <MapPin className="w-4 h-4 shrink-0 mt-0.5" />
                          <div>
                            <p className="font-semibold mb-0.5">Alamat Pengiriman:</p>
                            <p>{item.alamat_pengiriman.penerima} ({item.alamat_pengiriman.telepon})</p>
                            <p>{item.alamat_pengiriman.alamat_lengkap} {item.alamat_pengiriman.kode_pos ? `- ${item.alamat_pengiriman.kode_pos}` : ''}</p>
                          </div>
                        </div>
                      )}

                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                        Daftar Produk ({productCount})
                      </p>

                      {item.products?.map((product, pIdx) => {
                        if (statusFilter !== 'semua' && (product.status || 'pending') !== statusFilter) return null;
                        return (
                          <ProductSubCard
                            key={product._id || pIdx}
                            product={product}
                            productIndex={pIdx}
                            sampleId={item.id}
                            canEdit={canEdit && !isSelectionMode}
                            onStatusChange={(pidx, newStatus) => handleProductStatusChange(item, pidx, newStatus)}
                            onAccClick={(pidx) => handleAccClick(item, pidx)}
                            onDikerjakanClick={(pidx) => handleDikerjakanClick(item, pidx)}
                            onEditClick={(pidx) => { 
                              setEditProductIndex(pidx);
                              setEditItem(item); 
                              setShowForm(true); 
                            }}
                            onPrintClick={(pidx) => onPrintClick(item, pidx)}
                            isAccing={accingProductKey === `${item.id}_${pIdx}`}
                            currentMemberName={member?.full_name || 'System'}
                          />
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}

          {/* ── Pagination Controls ── */}
          {filtered.length > 0 && (
            <div className="px-6 py-4 border border-slate-100 rounded-2xl bg-white flex flex-wrap items-center justify-between gap-4 mt-6">
              <div className="flex items-center gap-3">
                <span className="text-[11px] font-bold text-slate-500">Row per Page</span>
                <div className="w-[80px]">
                  <Select value={String(itemsPerPage)} onValueChange={(val) => { setItemsPerPage(Number(val)); setCurrentPage(1); }}>
                    <SelectTrigger className="h-8 text-[11px] font-bold text-slate-700 bg-slate-50 border-slate-200/80 rounded-xl">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[5, 10, 25, 50].map(n => (
                        <SelectItem key={n} value={String(n)} className="text-[11px] font-bold">{n}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="flex items-center gap-1.5">
                <button 
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:bg-slate-50 hover:text-[#8C6208] disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-slate-400 transition-all font-bold text-sm"
                >
                  &lt;
                </button>
                
                {(() => {
                  const totalPages = Math.ceil(filtered.length / itemsPerPage);
                  const pages = [];
                  for(let i=1; i<=totalPages; i++) {
                    if (i === 1 || i === totalPages || (i >= currentPage - 1 && i <= currentPage + 1)) {
                      pages.push(
                        <button
                          key={i}
                          onClick={() => setCurrentPage(i)}
                          className={`w-8 h-8 flex items-center justify-center rounded-lg text-[11px] font-black transition-all ${
                            currentPage === i 
                              ? 'bg-[#8C6208] text-white shadow-md shadow-[#8C6208]/30' 
                              : 'text-slate-500 hover:bg-[#F9F5EC] hover:text-[#8C6208]'
                          }`}
                        >
                          {i}
                        </button>
                      );
                    } else if (i === currentPage - 2 || i === currentPage + 2) {
                      pages.push(<span key={i} className="text-slate-400 text-[10px] px-1">...</span>);
                    }
                  }
                  return pages;
                })()}
                
                <button 
                  onClick={() => setCurrentPage(p => Math.min(Math.ceil(filtered.length / itemsPerPage), p + 1))}
                  disabled={currentPage >= Math.ceil(filtered.length / itemsPerPage)}
                  className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:bg-slate-50 hover:text-[#8C6208] disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-slate-400 transition-all font-bold text-sm"
                >
                  &gt;
                </button>
              </div>
            </div>
          )}
        </div>
      )}
      
      {/* Floating Back to Top Button */}
      <button 
        onClick={scrollToTop}
        className="fixed bottom-[80px] md:bottom-8 right-6 md:right-8 z-50 w-12 h-12 bg-[#8C6208] hover:bg-[#6A4A06] text-white rounded-full shadow-xl shadow-[#8C6208]/30 flex items-center justify-center transition-transform hover:-translate-y-1 print:hidden border border-[#D4AF37]/30"
        title="Kembali ke Atas"
      >
        <ChevronUp className="w-6 h-6" />
      </button>

      {/* ASA POP-UP MODAL: ACC & SIMPAN KE DATABASE BRAND */}
      {accPrompt && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-slate-900/70 backdrop-blur-xs animate-in fade-in duration-200 overflow-y-auto">
          <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full overflow-hidden border border-[#E6D5B8] my-8 animate-in zoom-in-95 duration-200 flex flex-col max-h-[90vh]">
            
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-[#FFFDF9] via-[#FDF8EE] to-[#FFF3E0] p-5 border-b border-[#E6D5B8]/60 flex gap-3.5 items-center shrink-0">
              <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-[#D4AF37] shadow-sm bg-white shrink-0 flex items-center justify-center relative">
                <img 
                  src="/asa-profile.png" 
                  alt="ASA" 
                  className="w-full h-full object-cover" 
                  onError={(e) => { e.target.src = 'https://ui-avatars.com/api/?name=Asa&background=0D8ABC&color=fff'; }} 
                />
                <div className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 bg-green-500 rounded-full border-2 border-white animate-pulse" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-black text-[#302621] text-base md:text-lg">Konfirmasi Asa AI</h3>
                  <span className="text-[10px] bg-[#8C6208]/10 text-[#8C6208] px-2.5 py-0.5 rounded-full font-extrabold border border-[#8C6208]/20 flex items-center gap-1">
                    <Sparkles className="w-3 h-3 text-[#8C6208]" /> SIMPAN DATABASE BRAND
                  </span>
                </div>
                <p className="text-xs text-slate-500 font-medium truncate mt-0.5">
                  Brand: <strong className="text-[#302621]">{accPrompt.sampleItem.nama_brand}</strong> • Varian: <strong className="text-[#8C6208]">{accPrompt.product.nama_produk}</strong>
                </p>
              </div>
            </div>

            {/* Modal Body - Scrollable */}
            <div className="p-5 md:p-6 space-y-5 overflow-y-auto flex-1 text-slate-800">
              
              {/* Asa Bubble Message */}
              <div className="bg-[#FFFDF9] border border-[#E6D5B8] p-4 rounded-2xl shadow-2xs relative">
                <p className="text-xs md:text-sm text-[#302621] font-medium leading-relaxed">
                  Halo <b>{sapaan}</b>! Formulasi sample untuk produk <b>"{accPrompt.product.nama_produk}"</b> (Brand: <b>{accPrompt.sampleItem.nama_brand}</b>) mau di-ACC. Asa siap bantu simpan racikan dan pilihan packaging ini langsung ke <b>Database Brand</b> biar nanti pas bikin invoice tinggal pilih!
                </p>
              </div>

              {/* Status Jika Varian Sudah Ada di Database */}
              {accPrompt.isExisting ? (
                <div className="bg-amber-50/80 border border-amber-200 rounded-2xl p-4 space-y-3">
                  <div className="flex items-start gap-2.5">
                    <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                    <div>
                      <span className="text-xs font-bold text-amber-900 block">Varian ini sudah ada di Database Brand</span>
                      <span className="text-[11px] text-amber-700">Pilih apakah kamu ingin menimpa formula yang sudah ada atau menyimpannya sebagai varian baru.</span>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2.5 pt-1">
                    <button
                      type="button"
                      onClick={() => setAccPrompt({ ...accPrompt, saveMode: 'overwrite' })}
                      className={`p-3 rounded-xl border text-left transition-all ${
                        accPrompt.saveMode === 'overwrite'
                          ? 'bg-amber-100/80 border-amber-400 text-amber-950 font-bold shadow-xs'
                          : 'bg-white border-amber-200 text-amber-800 hover:bg-amber-50'
                      }`}
                    >
                      <div className="text-xs font-bold">Timpa Formula Lama</div>
                      <div className="text-[10px] opacity-80 mt-0.5">Perbarui formula & packaging yang tersimpan di database brand</div>
                    </button>
                    <button
                      type="button"
                      onClick={() => setAccPrompt({ ...accPrompt, saveMode: 'new' })}
                      className={`p-3 rounded-xl border text-left transition-all ${
                        accPrompt.saveMode === 'new'
                          ? 'bg-amber-100/80 border-amber-400 text-amber-950 font-bold shadow-xs'
                          : 'bg-white border-amber-200 text-amber-800 hover:bg-amber-50'
                      }`}
                    >
                      <div className="text-xs font-bold">Buat Varian Baru</div>
                      <div className="text-[10px] opacity-80 mt-0.5">Simpan sebagai data varian terpisah di database brand</div>
                    </button>
                  </div>
                </div>
              ) : (
                <div className="bg-emerald-50/70 border border-emerald-200 rounded-2xl p-3.5 flex items-center gap-2.5">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                  <span className="text-xs font-medium text-emerald-800">
                    Varian baru ini akan otomatis didaftarkan ke <b>Database Brand: {accPrompt.sampleItem.nama_brand}</b>.
                  </span>
                </div>
              )}

              {/* Section Kemasan & Packaging */}
              <div className="bg-slate-50/90 border border-slate-200 rounded-2xl p-4 md:p-5 space-y-4">
                <div className="flex items-center justify-between flex-wrap gap-2 pb-2 border-b border-slate-200">
                  <div className="flex items-center gap-2">
                    <Package className="w-4 h-4 text-slate-700 shrink-0" />
                    <div>
                      <span className="text-xs font-bold text-slate-900 block">Pilihan Kemasan & Packaging (Database Brand)</span>
                      <span className="text-[10px] text-slate-500">Pilih packaging default untuk produk ini (bisa dikosongkan / dari client)</span>
                    </div>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => setAccPrompt({ ...accPrompt, isFullset: !accPrompt.isFullset })}
                    className="h-7 text-xs font-bold border-[#E6D5B8] bg-white text-[#8C6208] hover:bg-[#F9F5EC]"
                  >
                    {accPrompt.isFullset ? 'Pilih Terpisah' : 'Gunakan Paket Fullset'}
                  </Button>
                </div>

                {accPrompt.isFullset ? (
                  <div className="space-y-2 bg-emerald-50/50 border border-emerald-100 rounded-xl p-3.5">
                    <Label className="text-xs font-semibold text-emerald-900 block">Pilih Paket Fullset</Label>
                    <SearchableSelect
                      options={[
                        { value: '', label: '— Belum Ditentukan / Kosongkan —' },
                        { value: 'Dari Client', label: '📦 Dari Client (Kemasan Bawa Sendiri)' },
                        ...fullsetList.map(f => ({
                          value: f.nama,
                          label: `${f.nama} (${f.ukuran_label_ml || ''}ml)`
                        }))
                      ]}
                      value={accPrompt.fullsetNama || ''}
                      onChange={(val) => setAccPrompt({ ...accPrompt, fullsetNama: val })}
                      placeholder="Cari kemasan fullset..."
                      className="bg-white border-[#E6D5B8]"
                    />
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs font-semibold text-slate-700">Botol / Wadah</Label>
                      <SearchableSelect
                        options={[
                          { value: '', label: '— Belum Ditentukan / Kosongkan —' },
                          { value: 'Dari Client', label: '📦 Dari Client (Bawa Sendiri)' },
                          ...botolList.map(b => ({
                            value: b.nama,
                            label: `${b.nama} (${b.ukuran_label_ml}ml)`
                          }))
                        ]}
                        value={accPrompt.botolNama || ''}
                        onChange={(val) => setAccPrompt({ ...accPrompt, botolNama: val })}
                        placeholder="Pilih botol..."
                        className="bg-white border-[#E6D5B8]"
                      />
                    </div>

                    <div className="space-y-1.5">
                      <Label className="text-xs font-semibold text-slate-700">Tutup</Label>
                      <SearchableSelect
                        options={[
                          { value: '', label: '— Belum Ditentukan / Kosongkan —' },
                          { value: 'Dari Client', label: '📦 Dari Client (Bawa Sendiri)' },
                          ...tutupList.map(t => ({
                            value: t.nama,
                            label: t.nama
                          }))
                        ]}
                        value={accPrompt.tutupNama || ''}
                        onChange={(val) => setAccPrompt({ ...accPrompt, tutupNama: val })}
                        placeholder="Pilih tutup..."
                        className="bg-white border-[#E6D5B8]"
                      />
                    </div>

                    <div className="space-y-1.5">
                      <Label className="text-xs font-semibold text-slate-700">Spray / Pump</Label>
                      <SearchableSelect
                        options={[
                          { value: '', label: '— Belum Ditentukan / Kosongkan —' },
                          { value: 'Dari Client', label: '📦 Dari Client (Bawa Sendiri)' },
                          ...sprayList.map(s => ({
                            value: s.nama,
                            label: s.nama
                          }))
                        ]}
                        value={accPrompt.sprayNama || ''}
                        onChange={(val) => setAccPrompt({ ...accPrompt, sprayNama: val })}
                        placeholder="Pilih spray..."
                        className="bg-white border-[#E6D5B8]"
                      />
                    </div>
                  </div>
                )}

                {/* Ukuran Botol */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-1">
                  <div className="space-y-1.5">
                    <Label className="text-xs font-semibold text-slate-700">Ukuran Label / Final (ml) *</Label>
                    <Input
                      type="number"
                      value={accPrompt.ukuranLabelMl}
                      onChange={(e) => setAccPrompt({ ...accPrompt, ukuranLabelMl: e.target.value })}
                      placeholder="Contoh: 50"
                      className="bg-white border-[#E6D5B8]"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-semibold text-slate-700">Ukuran Aktual Botol (ml) [Opsional]</Label>
                    <Input
                      type="number"
                      value={accPrompt.ukuranAktualMl}
                      onChange={(e) => setAccPrompt({ ...accPrompt, ukuranAktualMl: e.target.value })}
                      placeholder="Contoh: 53"
                      className="bg-white border-[#E6D5B8]"
                    />
                  </div>
                </div>
              </div>

              {/* Komposisi Racikan yang di-ACC */}
              <div className="bg-slate-50/70 border border-slate-200 rounded-2xl p-4 space-y-2.5">
                <div className="flex items-center gap-2">
                  <FlaskConical className="w-4 h-4 text-[#8C6208] shrink-0" />
                  <span className="text-xs font-bold text-slate-900">Komposisi Racikan yang Di-ACC ({(accPrompt.product.racikan || []).length} Bahan)</span>
                </div>
                <div className="rounded-xl border border-slate-200 overflow-hidden bg-white">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50 border-b border-slate-200 text-slate-600">
                      <tr>
                        <th className="text-left px-3 py-2 font-bold">Kategori</th>
                        <th className="text-left px-3 py-2 font-bold">Nama Bahan</th>
                        <th className="text-left px-3 py-2 font-bold">Vendor</th>
                        <th className="text-right px-3 py-2 font-bold">%</th>
                        <th className="text-right px-3 py-2 font-bold">Vol ({accPrompt.ukuranLabelMl || 0}ml)</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {(accPrompt.product.racikan || []).map((r, i) => {
                        const pct = parseFloat(String(r.persentase).replace(',', '.')) || 0;
                        const vol = ((pct / 100) * (Number(accPrompt.ukuranLabelMl) || 0)).toFixed(2);
                        return (
                          <tr key={i} className="hover:bg-slate-50/80">
                            <td className="px-3 py-2 font-semibold text-[#8C6208]">{r.kategori_bahan || r.kategori || 'Bahan'}</td>
                            <td className="px-3 py-2 font-medium text-slate-800">{r.nama_bahan || r.bahan_nama || '—'}</td>
                            <td className="px-3 py-2 text-slate-500">{r.vendor || '—'}</td>
                            <td className="px-3 py-2 text-right font-bold text-slate-800">{pct}%</td>
                            <td className="px-3 py-2 text-right font-medium text-amber-700">{vol} ml</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

            </div>

            {/* Modal Footer */}
            <div className="bg-[#FFFDF9] border-t border-[#E6D5B8]/60 p-4 md:p-5 flex items-center justify-between gap-3 shrink-0">
              <Button
                variant="ghost"
                onClick={() => setAccPrompt(null)}
                className="text-xs font-semibold text-slate-600 hover:bg-slate-100"
              >
                Batal
              </Button>
              <Button
                onClick={handleManualAccSubmit}
                disabled={!accPrompt.ukuranLabelMl}
                className="gap-2 px-6 h-10 rounded-xl bg-gradient-to-r from-[#8C6208] to-[#6a4a06] hover:from-[#6a4a06] hover:to-[#4a3404] text-white font-bold shadow-md hover:shadow-lg transition-all"
              >
                <Check className="w-4 h-4" />
                <span>Simpan ke Database Brand & ACC</span>
              </Button>
            </div>

          </div>
        </div>
      )}

      {/* ASA INVOICE SYNC CONFIRMATION MODAL */}
      {invoiceSyncPrompt && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-slate-900/70 backdrop-blur-xs animate-in fade-in duration-200 overflow-y-auto">
          <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full overflow-hidden border border-[#E6D5B8] my-8 animate-in zoom-in-95 duration-200 flex flex-col max-h-[90vh]">
            
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-[#FFFDF9] via-[#FDF8EE] to-[#FFF3E0] p-5 border-b border-[#E6D5B8]/60 flex gap-3.5 items-center shrink-0">
              <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-[#D4AF37] shadow-sm bg-white shrink-0 flex items-center justify-center">
                <img 
                  src="/asa-profile.png" 
                  alt="ASA" 
                  className="w-full h-full object-cover" 
                  onError={(e) => { e.target.src = 'https://ui-avatars.com/api/?name=Asa&background=0D8ABC&color=fff'; }} 
                />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-black text-[#302621] text-base md:text-lg">Konfirmasi Asa AI</h3>
                  <span className="text-[10px] bg-[#8C6208]/10 text-[#8C6208] px-2.5 py-0.5 rounded-full font-extrabold border border-[#8C6208]/20">
                    SINKRONISASI INVOICE
                  </span>
                </div>
                <p className="text-xs text-slate-500 font-medium truncate mt-0.5">
                  Update Formula & Audit Integrasi Inventori untuk Invoice #{invoiceSyncPrompt.targetPO.no_po || invoiceSyncPrompt.targetPO.invoice_number || invoiceSyncPrompt.targetPO.id}
                </p>
              </div>
            </div>

            {/* Modal Body - Scrollable */}
            <div className="p-5 md:p-6 space-y-5 overflow-y-auto flex-1 text-slate-800">
              
              {/* Asa Bubble Message */}
              <div className="bg-[#FFFDF9] border border-[#E6D5B8] p-4 rounded-2xl shadow-2xs relative">
                <p className="text-xs md:text-sm text-[#302621] font-medium leading-relaxed">
                  Halo <b>{sapaan}</b>! Formulasi sample untuk produk <b>"{invoiceSyncPrompt.product.nama_produk}"</b> telah di-ACC. Asa sudah memetakan integrasi setiap bahan dengan <b>Inventori Gudang</b> serta menghitung perubahan modal/HPP jika formula ini diterapkan ke <b>Invoice #{invoiceSyncPrompt.targetPO.no_po || invoiceSyncPrompt.targetPO.invoice_number || invoiceSyncPrompt.targetPO.id}</b>.
                </p>
              </div>

              {/* Perbandingan Modal Total & Per Pcs (HPP Analysis) */}
              <div className="bg-slate-50/90 border border-slate-200 rounded-2xl p-4 space-y-3">
                <div className="flex items-center justify-between flex-wrap gap-2 pb-2 border-b border-slate-200">
                  <div className="flex items-center gap-2">
                    <BarChart3 className="w-4 h-4 text-slate-700 shrink-0" />
                    <div>
                      <span className="text-xs font-bold text-slate-900 block">Analisa Selisih Biaya & Modal (HPP)</span>
                      <span className="text-[10px] text-slate-500">Estimasi total kebutuhan pesanan massal ({invoiceSyncPrompt.targetInvoiceProduct?.qty || 1000} pcs)</span>
                    </div>
                  </div>
                  
                  {invoiceSyncPrompt.deltaModal !== undefined && (
                    <span className={`text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1.5 border shadow-2xs ${
                      invoiceSyncPrompt.deltaModal > 0
                        ? 'bg-rose-50 text-rose-700 border-rose-200'
                        : invoiceSyncPrompt.deltaModal < 0
                        ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                        : 'bg-slate-100 text-slate-700 border-slate-200'
                    }`}>
                      {invoiceSyncPrompt.deltaModal > 0 ? (
                        <>
                          <TrendingUp className="w-3.5 h-3.5 text-rose-600" />
                          <span>
                            Modal Naik +{formatRupiah(invoiceSyncPrompt.deltaModal)} 
                            {invoiceSyncPrompt.deltaHppPerPcs ? ` (+${formatRupiah(invoiceSyncPrompt.deltaHppPerPcs)}/pcs)` : ''}
                          </span>
                        </>
                      ) : invoiceSyncPrompt.deltaModal < 0 ? (
                        <>
                          <TrendingDown className="w-3.5 h-3.5 text-emerald-600" />
                          <span>
                            Modal Turun -{formatRupiah(Math.abs(invoiceSyncPrompt.deltaModal))} 
                            {invoiceSyncPrompt.deltaHppPerPcs ? ` (-${formatRupiah(Math.abs(invoiceSyncPrompt.deltaHppPerPcs))}/pcs)` : ''}
                          </span>
                        </>
                      ) : (
                        <span>Modal Tetap (Rp 0 / pcs)</span>
                      )}
                    </span>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                  <div className="bg-white p-3 rounded-xl border border-slate-200/90 shadow-2xs space-y-1 min-w-0">
                    <span className="text-[10px] text-slate-500 block font-bold uppercase tracking-wider">Before (Modal Invoice Lama)</span>
                    <p className="font-bold text-slate-700 text-xs md:text-sm break-words">{formatRupiah(invoiceSyncPrompt.oldModal)}</p>
                    {invoiceSyncPrompt.oldHppPerPcs > 0 && (
                      <p className="text-[10px] text-slate-400 font-medium break-words">~ {formatRupiah(invoiceSyncPrompt.oldHppPerPcs)} / pcs</p>
                    )}
                  </div>
                  
                  <div className="bg-white p-3 rounded-xl border border-slate-200/90 shadow-2xs space-y-1 min-w-0">
                    <span className="text-[10px] text-slate-500 block font-bold uppercase tracking-wider">After (Modal Racikan Baru)</span>
                    <p className={`font-bold text-xs md:text-sm break-words ${
                      invoiceSyncPrompt.deltaModal > 0 
                        ? 'text-rose-600' 
                        : invoiceSyncPrompt.deltaModal < 0 
                        ? 'text-emerald-700' 
                        : 'text-slate-800'
                    }`}>
                      {formatRupiah(invoiceSyncPrompt.newModal)}
                    </p>
                    {invoiceSyncPrompt.newHppPerPcs > 0 && (
                      <p className={`text-[10px] font-medium break-words ${invoiceSyncPrompt.deltaModal > 0 ? 'text-rose-500' : 'text-emerald-600'}`}>
                        ~ {formatRupiah(invoiceSyncPrompt.newHppPerPcs)} / pcs
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* LIST UPDATE INVOICE: Before vs After & Tanda Terintegrasi Inventory */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="text-xs font-bold text-slate-800 uppercase tracking-wider flex items-center gap-1.5">
                    <FlaskConical className="w-3.5 h-3.5 text-slate-700" />
                    <span>Rincian Perubahan Komposisi & Integrasi Gudang</span>
                  </h4>
                  <span className="text-[10px] text-slate-500 font-medium">
                    {invoiceSyncPrompt.newRacikan?.length || 0} bahan pada sample final
                  </span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  
                  {/* KOLOM KIRI: BEFORE (Invoice Sebelumnya) */}
                  <div className="bg-slate-50/80 rounded-2xl p-3.5 border border-slate-200 space-y-2.5">
                    <div className="flex items-center justify-between pb-1.5 border-b border-slate-200">
                      <span className="text-[11px] font-bold text-slate-600">Sebelumnya di Invoice</span>
                      <Badge variant="outline" className="text-[10px] bg-white text-slate-600 border-slate-300">Draft Lama</Badge>
                    </div>

                    {(invoiceSyncPrompt.oldRacikan || []).length > 0 ? (
                      <div className="space-y-2">
                        {invoiceSyncPrompt.oldRacikan.map((r, idx) => (
                          <div key={idx} className="bg-white p-2.5 rounded-xl border border-slate-200/80 text-xs flex justify-between items-center shadow-2xs">
                            <div className="min-w-0 pr-2">
                              <p className="font-semibold text-slate-700 truncate">{r.nama_bahan || r.bahan_nama || 'Bahan'}</p>
                              <p className="text-[10px] text-slate-400">{r.vendor ? `Vendor: ${r.vendor}` : r.kategori_bahan || 'Bahan'}</p>
                            </div>
                            <span className="font-bold text-slate-600 text-xs shrink-0 bg-slate-100 px-2 py-0.5 rounded-md border border-slate-200">
                              {r.persentase}%
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-slate-400 italic py-4 text-center">Belum ada rincian racikan sebelumnya di invoice.</p>
                    )}
                  </div>

                  {/* KOLOM KANAN: AFTER (Sample Final yang di-ACC + Status Integrasi) */}
                  <div className="bg-white rounded-2xl p-3.5 border border-slate-200 space-y-2.5 shadow-2xs">
                    <div className="flex items-center justify-between pb-1.5 border-b border-slate-200">
                      <span className="text-[11px] font-bold text-slate-800">Sample Final (Yang di-ACC)</span>
                      <Badge variant="outline" className="text-[10px] bg-slate-50 text-slate-700 border-slate-300 font-bold">
                        Siap Sync
                      </Badge>
                    </div>

                    {(invoiceSyncPrompt.newRacikan || []).length > 0 ? (
                      <div className="space-y-2">
                        {invoiceSyncPrompt.newRacikan.map((r, idx) => (
                          <div key={idx} className="bg-slate-50/50 p-2.5 rounded-xl border border-slate-200/80 text-xs shadow-2xs space-y-1.5">
                            <div className="flex justify-between items-start">
                              <div className="min-w-0 pr-2">
                                <p className="font-bold text-slate-800 truncate">{r.nama_bahan || 'Bahan'}</p>
                                <p className="text-[10px] text-slate-500">
                                  {r.vendor ? `Vendor: ${r.vendor}` : r.kategori_bahan || 'Bahan'}
                                  {r.volume_ml ? ` • ${formatDecimalNumber(r.volume_ml)} ml` : ''}
                                </p>
                              </div>
                              <span className="font-bold text-slate-700 text-xs shrink-0 bg-white px-2 py-0.5 rounded-md border border-slate-200">
                                {r.persentase}%
                              </span>
                            </div>

                            {/* Badge Integrasi Inventori Gudang (Clean 1-tone monochromatic) */}
                            <div className="pt-1 border-t border-slate-200/60 flex items-center justify-between flex-wrap gap-1">
                              {r.isIntegrated ? (
                                <span className="inline-flex items-center gap-1.5 text-[10px] font-medium text-slate-700 bg-white px-2 py-0.5 rounded-md border border-slate-200 shadow-2xs">
                                  <CheckCircle2 className="w-3 h-3 text-emerald-600 shrink-0" />
                                  <span>Terintegrasi {r.integratedType || 'Inventori'}</span>
                                  {r.stok !== null && (
                                    <span className="text-slate-500 font-normal">(Sisa: {formatDecimalNumber(r.stok)} {r.satuan})</span>
                                  )}
                                </span>
                              ) : r.dari_client ? (
                                <span className="inline-flex items-center gap-1.5 text-[10px] font-medium text-slate-600 bg-white px-2 py-0.5 rounded-md border border-slate-200">
                                  <span className="w-1.5 h-1.5 rounded-full bg-slate-400 shrink-0"></span>
                                  <span>Dari Client (Tidak potong gudang)</span>
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1.5 text-[10px] font-medium text-amber-800 bg-amber-50/70 px-2 py-0.5 rounded-md border border-amber-200">
                                  <AlertTriangle className="w-3 h-3 text-amber-700 shrink-0" />
                                  <span>Belum Terdaftar di Inventori (Perlu Pengadaan)</span>
                                </span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-slate-400 italic py-4 text-center">Belum ada komposisi racikan.</p>
                    )}
                  </div>

                </div>
              </div>

            </div>

            {/* Modal Footer Actions */}
            <div className="p-4 bg-slate-50 flex flex-wrap justify-end gap-2 border-t border-slate-100 shrink-0">
              <Button
                variant="ghost"
                className="text-slate-500 hover:bg-slate-200/50 text-xs font-semibold rounded-xl"
                onClick={() => setInvoiceSyncPrompt(null)}
              >
                Batal
              </Button>
              <Button
                variant="outline"
                className="text-slate-700 bg-white hover:bg-slate-100 text-xs font-bold border-slate-200 rounded-xl"
                onClick={() => {
                  commitAcc({ ...invoiceSyncPrompt, updateInvoice: false });
                }}
              >
                Hanya ACC Sample
              </Button>
              <Button
                className="bg-[#8C6208] text-white hover:bg-[#725007] text-xs font-black rounded-xl gap-1.5 shadow-md px-4 py-2"
                onClick={() => {
                  commitAcc({ ...invoiceSyncPrompt, updateInvoice: true });
                }}
              >
                <CheckCircle2 className="w-4 h-4 text-amber-300" />
                Ya, Update Invoice & Terapkan Formula
              </Button>
            </div>

          </div>
        </div>
      )}

      {showForm && (
        <SampleForm
          item={editItem}
          focusProductIndex={editProductIndex}
          onClose={() => { setShowForm(false); setEditItem(null); setEditProductIndex(undefined); }}
          onSaved={() => { setShowForm(false); setEditItem(null); setEditProductIndex(undefined); qc.invalidateQueries({ queryKey: ['sample-racikan'] }); }}
        />
      )}

      {/* Hidden A4 Print Container (Portaled to body to isolate from app layout) */}
      {batchPrintItems.length > 0 && createPortal(
        <div id="print-container" className="hidden print:block print:w-full print:bg-white print:m-0 print:p-0">
          <div 
            className="box-border mx-auto"
            style={{
              width: '210mm',
              minHeight: '297mm',
              padding: '10mm 1mm',
              display: 'grid',
              gridTemplateColumns: 'repeat(5, 40mm)',
              gridAutoRows: '20mm',
              justifyContent: 'center',
              alignContent: 'start',
              columnGap: '2mm',
              rowGap: '2mm',
              backgroundColor: 'white'
            }}
          >
            <style type="text/css" media="print">
              {`
                #root { display: none !important; }
                @page {
                  size: A4 portrait;
                  margin: 0;
                }
                body {
                  margin: 0 !important;
                  padding: 0 !important;
                  background: white !important;
                  -webkit-print-color-adjust: exact !important;
                  print-color-adjust: exact !important;
                }
              `}
            </style>
            {batchPrintItems.map((item, index) => (
              <SampleLabelSticker 
                key={index} 
                sample={item.sample} 
                product={item.product} 
              />
            ))}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

export default function SampleRacikanPage() {
  const [activeTab, setActiveTab] = useState("pembuatan");

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-display font-bold text-foreground">Sample</h1>
        <p className="text-muted-foreground text-sm mt-1">Manajemen sample pengujian, tes SPL, aroma & evaluasi sample</p>
      </div>

      <div className="flex gap-2 p-1.5 bg-muted/30 rounded-2xl border border-border/50 w-fit">
        <button
          onClick={() => setActiveTab("pembuatan")}
          className={`px-5 py-2 rounded-xl text-sm font-semibold transition-all ${
            activeTab === "pembuatan" ? "bg-primary text-primary-foreground shadow-sm" : "text-muted-foreground hover:bg-muted/50 hover:text-foreground"
          }`}
        >
          Pembuatan Sample
        </button>
        <button
          onClick={() => setActiveTab("bibit")}
          className={`px-5 py-2 rounded-xl text-sm font-semibold transition-all ${
            activeTab === "bibit" ? "bg-primary text-primary-foreground shadow-sm" : "text-muted-foreground hover:bg-muted/50 hover:text-foreground"
          }`}
        >
          Data Bibit Sample
        </button>
      </div>

      {activeTab === "pembuatan" && <SampleTab />}
      {activeTab === "bibit" && <DataBibitSampleTab />}
    </div>
  );
}
